import React, { useState } from "react";
import { useEffect } from "react";

import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  Alert,
  AlertTitle,
  Pagination,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import adminAxios from "../../adminApi/adminApi";
import { useAdminAuth } from "../../hooks/AdminAuthProvider";
import { GET_CAMPAIGN_TABLE_DATA } from "../../config/urls";
import CustomPagination from "../../common/custom-pagination";
import CustomTabs from "../../common/custom-tabs";

const CHANNEL_LABELS = { whatsapp: "WhatsApp", rcs: "RCS" };

// Single-contact sends get their own table, apart from audience (segment / CSV / retry / API ...) campaigns
const VIEW_CAMPAIGNS = "campaigns";
const VIEW_SINGLE = "single";
const isSingleBroadcast = (campaign) => campaign?.audience === "single";

const MarketingCampaignTab = ({ tenants, capitalize, convertTimestamp }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const { isAdminTokenValid, adminLogout } = useAdminAuth();
  const [campaignsData, setCampaignsData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [view, setView] = useState(VIEW_CAMPAIGNS);
  const isSingleView = view === VIEW_SINGLE;

  const query = searchQuery.trim().toLowerCase();
  // Rows matching search and filters, in both tables
  const matchingCampaigns = campaignsData.filter((campaign) => {
    const matchesTenant =
      selectedTenant === "all" || campaign?.tenant_id === selectedTenant;
    const matchesSearch =
      !query ||
      [
        campaign?.tenant_name,
        campaign?.campaign_name,
        campaign?.broadcast_name,
        CHANNEL_LABELS[campaign?.channel],
      ].some((value) => value?.toLowerCase().includes(query));
    const matchesChannel =
      channelFilter === "all" || campaign?.channel === channelFilter;
    const matchesStatus =
      statusFilter === "all" ||
      campaign.status === statusFilter ||
      // old rows were stored as "error"
      (statusFilter === "failed" && campaign.status === "error");
    return matchesTenant && matchesSearch && matchesChannel && matchesStatus;
  });
  const singleCount = matchingCampaigns.filter(isSingleBroadcast).length;
  const filteredCampaigns = matchingCampaigns.filter(
    (campaign) => isSingleBroadcast(campaign) === isSingleView
  );
  const viewTotal = campaignsData.filter(
    (campaign) => isSingleBroadcast(campaign) === isSingleView
  ).length;
  const currentCampaignItems = filteredCampaigns.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );


  const getStatusColor = (status) => {
    switch (status) {
      case "Success":
        return "success";
      case "In Progress":
        return "info";
      case "Scheduled":
        return "secondary";
      case "Created":
        return "default";
      case "Error":
      case "Failed":
        return "error";
      default:
        return "default";
    }
  };

  // Reset to the first page when filters change
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedTenant, statusFilter, channelFilter, view]);

  const fetchCampaignData = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get(GET_CAMPAIGN_TABLE_DATA);
      setCampaignsData(response.data.campaign_data);
      setError(null);
    } catch (err) {
      if (
        err.message === "Token expired" ||
        err.message === "No access token"
      ) {
        // Already handled by interceptor
        return;
      }
      setError(err.response?.data?.detail || "Failed to fetch tenants");
      console.error("Error fetching tenants:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check token validity before fetching
    if (!isAdminTokenValid()) {
      adminLogout();
      return;
    }
    fetchCampaignData();
  }, []);

  const statusDictionary = {
    success: "Success",
    error: "Error",
    scheduled: "Scheduled",
    in_progress: "In Progress",
    created: "Created",
    cancelled: "Cancelled",
    failed: "Failed",
  };
  return (
    <Paper>
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          Marketing Campaigns & Broadcasts
        </Typography>
        <Typography variant="body2" color="text.secondary">
          WhatsApp and RCS broadcasts from the last 30 days, plus running and
          upcoming ones
        </Typography>
        <CustomTabs
          value={view}
          onChange={(event, newView) => setView(newView)}
          tabs={[
            {
              value: VIEW_CAMPAIGNS,
              label: `Audience & Scheduled Campaigns (${
                matchingCampaigns.length - singleCount
              })`,
            },
            {
              value: VIEW_SINGLE,
              label: `Single / Quick Broadcasts (${singleCount})`,
            },
          ]}
          sx={{ mt: 2, maxWidth: 720 }}
        />
      </Box>
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tenant, campaign, broadcast or channel..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Tenant Filter</InputLabel>
              <Select
                value={selectedTenant}
                label="Tenant Filter"
                onChange={(e) => setSelectedTenant(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <FilterIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All Tenants</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Channel Filter</InputLabel>
              <Select
                value={channelFilter}
                label="Channel Filter"
                onChange={(e) => setChannelFilter(e.target.value)}
              >
                <MenuItem value="all">All Channels</MenuItem>
                {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="success">Success</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Showing {filteredCampaigns.length} of {viewTotal}{" "}
          {isSingleView ? "single broadcasts" : "campaign broadcasts"}
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Tenant Name</strong>
              </TableCell>
              <TableCell>
                <strong>Campaign Name</strong>
              </TableCell>
              <TableCell>
                <strong>Broadcast Name</strong>
              </TableCell>
              <TableCell>
                <strong>Channel Type</strong>
              </TableCell>
              <TableCell>
                <strong>Type</strong>
              </TableCell>
              {!isSingleView && (
                <TableCell>
                  <strong>Audience</strong>
                </TableCell>
              )}
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              {!isSingleView && (
                <TableCell>
                  <strong>Broadcast Contact</strong>
                </TableCell>
              )}
              <TableCell>
                <strong>Broadcast Time / Next Run</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentCampaignItems.map((campaign) => (
              <TableRow key={campaign?.id} hover>
                <TableCell>{campaign?.tenant_name}</TableCell>
                <TableCell>{campaign?.campaign_name || "—"}</TableCell>
                <TableCell>{campaign?.broadcast_name || "—"}</TableCell>
                <TableCell>
                  <Chip
                    label={CHANNEL_LABELS[campaign?.channel] || "—"}
                    size="small"
                    color={campaign?.channel === "rcs" ? "info" : "success"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={campaign?.scheduled_type}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </TableCell>
                {!isSingleView && (
                  <TableCell>
                    <Chip
                      label={campaign.audience}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </TableCell>
                )}
                <TableCell>
                  <Chip
                    label={capitalize(statusDictionary[campaign.status])}
                    size="small"
                    color={getStatusColor(statusDictionary[campaign.status])}
                  />
                </TableCell>
                {!isSingleView && (
                  <TableCell>
                    {campaign?.broadcast_contact_count?.toLocaleString() || 0}
                  </TableCell>
                )}
                <TableCell>
                  {convertTimestamp(campaign.scheduled_time)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredCampaigns.length > 0 && (
        <Box sx={{ borderTop: 1, borderColor: "divider" }}>
          <CustomPagination
            count={filteredCampaigns.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(e.target.value);
              setPage(0);
            }}
          />
        </Box>
      )}
      {!loading && filteredCampaigns.length === 0 && (
        <Box sx={{ p: 8, textAlign: "center" }}>
          <ErrorIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No campaigns found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MarketingCampaignTab;
